import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import * as XLSX from 'xlsx'

// Import employee images
import alexanderPic from './images/employees/alexander.jpg'
import belmonteLogo from './images/employees/belmonte-logo.jpg'
import bonafideLogo from './images/employees/bona-fide-logo.jpg'
import eirikPic from './images/employees/eirik.jpg'
import ivanPic from './images/employees/ivan.jpg'
import monicaPic from './images/employees/monica.jpg'
import petterPic from './images/employees/petter.jpg'

// pdfMake.vfs = pdfFonts.pdfMake.vfs

const ProductType = {
  Cider: 'Sider',
  NonAlcoholic: 'Alkoholfri',
  SparklingWine: 'Musserende vin',
  WhiteWine: 'Hvitvin',
  RoseWine: 'Rosévin',
  RedWine: 'Rødvin',
  StrongWine: 'Hetvin',
  DessertWine: 'Dessertvin',
  Liqour: 'Brennevin'
}

const Column = {
  SAPnr: 'SAPnr',
  EPDnr: 'EPDnr',
  ProductName: 'Produktnavn',
  Year: 'Årgang',
  Producer: 'Produsent',
  Importer: 'Importør',
  Volume: 'Vol',
  Price: 'Pris eks.mva *'
}

export default async (productsFile, stockFile) => {
  // Convert excel to array of objects
  let products = await getExcelAsArray(productsFile, 0)
  let stock = await getExcelAsArray(stockFile, 7)

  // Validate that we have all the required fields from the Excel files
  validateProducts(products)
  validateStock(stock)

  // Clean up master file data
  products = dataCleanup(products)
  stock = dataCleanup(stock)

  // Rename product types
  renameProductTypes(products)

  // Getters for categorized products
  const productsByType = getProductsByType(products)
  const productsByCountry = getProductsByCountry(products)
  const productsByRegion = getProductsByRegion(products)
  const regionsByCountry = getRegionsByCountry(products)
  const countriesByType = getCountriesByType(products)
  const productTypes = Object.values(ProductType)
  const pdfContent = []

  const sortedProducts = []

  // Rename the columns we want to export to PDF table.
  renameColumns(products)
  formatProductPrice(products)

  // Build front page and add it to PDF
  const frontPage = await buildFrontPage()
  pdfContent.push(frontPage)

  // Let's start building our PDF content
  // First loop, we'll go through all product types
  for (let i = 0; i < productTypes.length; i++) {
    const productType = productTypes[i]
    const typeProducts = productsByType.filter((product) => product.type === productType)
    const countries = distinctArray(countriesByType[productType]).sort()

    // Skip the product type if it doesn't have any products
    if (typeProducts.length === 0) continue

    const isFirstPage = i === 0

    let typeHeader
    if (!isFirstPage) {
      typeHeader = {
        text: productType,
        style: 'type',
        pageBreak: 'before'
      }
    } else {
      typeHeader = {
        text: productType,
        style: 'type'
      }
    }

    if (productType === ProductType.NonAlcoholic) {
      typeHeader = {
        text: productType,
        style: 'type'
      }
    }

    pdfContent.push(typeHeader)

    // Now let's loop over all countries for the current product type
    for (let j = 0; j < countries.length; j++) {
      const country = countries[j]
      const countryProducts = productsByCountry.find((product) => product.country === country).products
      const regions = distinctArray(regionsByCountry[country]).sort()
      const countryProductsCount = countryProducts.filter((product) => product.Hovedgruppe === productType).length

      const matchedProducts = countryProducts.filter((product) => {
        if (product.Hovedgruppe === productType && product.Navn === country) {
          return product
        }
      })

      // Skip the country if it doesn't have any products for the current product type
      if (matchedProducts.length === 0) {
        continue
      }

      // const countryHeader = {
      //   id: `country-${i}-${j}`,
      //   text: country,
      //   style: 'country',
      //   margin: [0, 0, 20, 0]
      // pageBreakBefore: function(currentNode) {
      //   if (currentNode.id === 'country-header' && (currentNode.pageNumbers.length !== 1 || currentNode.pageNumbers[0] !== currentNode.pages)) {
      //     return true
      //   } else {
      //     return false
      //   }
      // }
      // }

      const countryTable = {
        layout: 'noBorders',
        table: {
          headerRows: 1,
          keepWithHeaderRows: countryProductsCount > 50 ? 0 : 1,
          body: [
            [{ text: country, style: 'country' }]
          ]
        },
        pageBreakBefore: function() {
          return countryProductsCount > 50
        }
      }

      const countryUnderline = {
        canvas: [
          {
            type: 'line',
            x1: 0,
            y1: 5,
            x2: 595 - 2 * 40,
            y2: 5,
            lineWidth: 0.5
          }
        ],
        margin: [0, 0, 0, 15]
      }

      // pdfContent.push(countryHeader)
      pdfContent.push(countryUnderline)

      // At last, let's loop over regions connected to the current country.
      for (let k = 0; k < regions.length; k++) {
        const region = regions[k]
        const regionProducts = productsByRegion.find((product) => product.region === region).products

        const matchedProducts = regionProducts.filter((product) => {
          if (product.Hovedgruppe === productType && product.Navn === country) {
            return product
          }
        })

        // Skip the region if it doesn't have any products for the current product type and country.
        if (matchedProducts.length === 0) continue

        // Add products to sortedProducts so we can count them afterwards.
        sortedProducts.push(...matchedProducts)

        const productsTable = createProductsTable(matchedProducts, stock, region)

        // Add productsTable inside countryTable
        countryTable.table.body.push([productsTable])
      }

      pdfContent.push(countryTable)
    }
  }

  // Build product page for Sydonios products, and add it to PDF
  // const glassProducts = await buildProductPage()
  // pdfContent.push(glassProducts)

  validateResults(products, sortedProducts)

  buildPDF(pdfContent)
}

// Get all the product types from the master file
// const getProductTypes = (products) => {
//   const productTypes = distinctArrayOfObjects(products, 'Hovedgruppe')
//   return productTypes
// }

const validateResults = (input, output) => {
  const missingItems = []

  // Find missing items in output
  for (let i = 0; i < input.length; i++) {
    const product = input[i]

    const foundItem = output.find((item) => {
      return item.SAPnr === product.SAPnr
    })

    if (!foundItem) {
      missingItems.push(product)
    }
  }

  // Output validations
  // console.log('Input length: ', input.length)
  // console.log('Output length: ', output.length)
  // console.log('Missing items: ', missingItems)
}

/**
 * Clean up the excel data.
 *
 * @param {Array} data
 */
const dataCleanup = (data) => {
  const cleanedData = data.map((item) => {
    const newObject = Object.fromEntries(Object.entries(item).map(([key, value]) => {
      // Replace all whitespace characters with normal space.
      if (typeof value === 'string') {
        value = value.replace(/\s+/g, ' ')
      }

      // Set empty string to values that are only whitespace
      if (/^\s*$/.test(value)) {
        value = value.replace(/\s+/g, '')
      }

      return [key, value]
    }))

    return newObject
  })

  return cleanedData
}

/**
 * Validate that we have all the required fields from the master file.
 *
 * @param {Array} products
 * @returns {Boolean}
 */
const validateProducts = (products) => {
  const requiredColumns = [
    'Artikkelnr.',
    'PLU-nr.',
    'Artikkel',
    'Aargang',
    'Prod',
    'Artikkeltype',
    'Art. vekt',
    'Utpris'
  ]

  const columns = Object.keys(products[0])

  for (let i = 0; i < requiredColumns.length; i++) {
    if (!columns.includes(requiredColumns[i])) {
      throw new Error('Master file missing column: ' + requiredColumns[i])
    }
  }

  return true
}

/**
 * Validate that we have all the required fields from the stock file.
 *
 * @param {Array} stock
 * @returns {Boolean}
 */
const validateStock = (stock) => {
  const requiredColumns = [
    'EPD Number',
    'PCS'
  ]

  const columns = Object.keys(stock[0])

  for (let i = 0; i < requiredColumns.length; i++) {
    if (!columns.includes(requiredColumns[i])) {
      throw new Error('Stock file missing column: ' + requiredColumns[i])
    }
  }

  return true
}

/**
 * Build the front PDF page
 *
 * @returns {Object}
 */
const buildFrontPage = async () => {
  const belmonteLogoImage = await toDataURL(belmonteLogo)
  const bonafideLogoImage = await toDataURL(bonafideLogo)
  const alexanderImage = await toDataURL(alexanderPic)
  const eirikImage = await toDataURL(eirikPic)
  const ivanImage = await toDataURL(ivanPic)
  const monicaImage = await toDataURL(monicaPic)
  const petterImage = await toDataURL(petterPic)

  const salesPeopleTopRow = [
    {
      name: 'Monica Strand',
      email: 'monica@belmonte.no',
      phone: '+47 480 36 244',
      location1: 'Oslo / Vestfold / Buskerud',
      location2: 'Trøndelag / Sør-øst Akershus',
      image: monicaImage
    },
    {
      name: 'Alexander Sandli',
      email: 'alexander@belmonte.no',
      phone: '+47 932 65 348',
      location1: 'Oslo / Buskerud / Trøndelag',
      location2: 'Syd-vest Akershus',
      image: alexanderImage
    },
    {
      name: 'Eirik Søreide',
      email: 'eirik@belmonte.no',
      phone: '+47 413 45 486',
      location1: 'Oslo / Innlandet / Agder',
      location2: 'Telemark / Nord Akershus',
      image: eirikImage
    }
  ]

  const salesPeopleBottomRow = [
    {
      name: 'Petter Svanæs',
      email: 'petter.svanaes@belmonte.no',
      phone: '+47 482 93 943',
      location1: 'Oslo / Østfold / Nordland',
      location2: 'Troms / Finnmark',
      image: petterImage
    },
    {
      name: 'Ivan Zednik',
      email: 'ivan@belmonte.no',
      phone: '+47 909 81 854',
      location1: 'Oslo / Vestland',
      location2: 'Møre og Romsdal',
      image: ivanImage
    }
  ]

  const topRow = []
  const bottomRow = []

  // Add top row salespeople
  for (let i = 0; i < salesPeopleTopRow.length; i++) {
    const salesPerson = salesPeopleTopRow[i]

    const column = [
      {
        image: salesPerson.image,
        width: 160,
        height: 199.79,
        alignment: 'center',
        margin: [0, 0, 0, 20]
      },
      {
        text: salesPerson.name,
        fontSize: 10,
        alignment: 'center'
      },
      {
        text: salesPerson.email,
        link: 'mailto:' + salesPerson.email,
        fontSize: 10,
        decoration: 'underline',
        alignment: 'center'
      },
      {
        text: salesPerson.phone,
        link: 'tel:' + salesPerson.phone,
        fontSize: 10,
        decoration: 'underline',
        alignment: 'center'
      },
      {
        text: salesPerson.location1,
        fontSize: 10,
        alignment: 'center'
      },
      {
        text: salesPerson.location2,
        fontSize: 10,
        alignment: 'center'
      }
    ]

    topRow.push(column)
  }

  for (let i = 0; i < salesPeopleBottomRow.length; i++) {
    const salesPerson = salesPeopleBottomRow[i]

    const column = [
      {
        image: salesPerson.image,
        width: 160,
        height: 199.79,
        alignment: 'center',
        margin: [0, 0, 0, 15]
      },
      {
        text: salesPerson.name,
        fontSize: 10,
        alignment: 'center'
      },
      {
        text: salesPerson.email,
        link: 'mailto:' + salesPerson.email,
        fontSize: 10,
        decoration: 'underline',
        alignment: 'center'
      },
      {
        text: salesPerson.phone,
        link: 'tel:' + salesPerson.phone,
        fontSize: 10,
        decoration: 'underline',
        alignment: 'center'
      },
      {
        text: salesPerson.location1,
        fontSize: 10,
        alignment: 'center'
      },
      {
        text: salesPerson.location2,
        fontSize: 10,
        alignment: 'center',
        margin: [0, 0, 0, 20]
      }
    ]

    bottomRow.push(column)
  }

  const currentDate = new Date().toLocaleDateString('no-NO')

  const frontPage = [
    {
      image: belmonteLogoImage,
      width: 88,
      height: 160,
      margin: [0, 100, 0, 0],
      alignment: 'center'
    },
    {
      image: bonafideLogoImage,
      width: 90,
      height: 31.83,
      margin: [0, 20, 0, 100],
      alignment: 'center'
    },
    {
      text: `Prisliste per ${currentDate}`,
      alignment: 'center',
      fontSize: 13,
      pageBreak: 'after'
    },
    {
      text: 'Ta kontakt med en av våre salgskonsulenter.',
      margin: [0, 0, 0, 50],
      alignment: 'center',
      fontSize: 16,
      bold: true
    },
    {
      columns: topRow,
      margin: [0, 0, 0, 40]
    },
    {
      columns: bottomRow,
      pageBreak: 'after'
    }
  ]

  return frontPage
}

/**
 * Turn Excel file into array of objects (rows).
 *
 * @param {Object} file
 * @param {number} range
 * @returns
 */
const getExcelAsArray = (file, range) => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader()

    fileReader.readAsBinaryString(file)

    fileReader.onload = async (event) => {
      const data = event.target.result
      const workbook = await XLSX.read(data, { type: 'binary' })

      workbook.SheetNames.forEach((sheet) => {
        const rowObject = XLSX.utils.sheet_to_json(workbook.Sheets[sheet], { range, defval: null })
        resolve(rowObject)
      })
    }
    fileReader.onerror = reject
  })
}

/**
 * Categorize all products by type.
 *
 * @param {Array} products
 * @returns {Array} productsByType
 */
const getProductsByType = (products) => {
  let productsByType = []

  const types = distinctArrayOfObjects(products, 'Hovedgruppe')

  for (let i = 0; i < types.length; i++) {
    const type = types[i]

    const typeProducts = {
      type: type,
      products: []
    }

    products.filter((product) => {
      // Insert products with type
      if (product.Hovedgruppe === type) {
        typeProducts.products = [...typeProducts.products, product]
      }
    })

    productsByType = [...productsByType, typeProducts]
  }

  return productsByType
}
/**
 * Categorize all products by country.
 *
 * @param {Array} products
 * @returns {Array} productsByCountry
 */
const getProductsByCountry = (products) => {
  let productsByCountry = []

  const countries = distinctArrayOfObjects(products, 'Navn')

  for (let i = 0; i < countries.length; i++) {
    const country = countries[i]

    const countryProducts = {
      country: country,
      products: []
    }

    products.filter((product) => {
      // Insert products with country
      if (product.Navn === country) {
        countryProducts.products = [...countryProducts.products, product]
      }
    })

    productsByCountry = [...productsByCountry, countryProducts]
  }

  return productsByCountry
}

/**
 * Categorize all products by region.
 *
 * @param {Array} products
 * @returns {Array} productsByRegion
 */
const getProductsByRegion = (products) => {
  let productsByRegion = []

  const regions = distinctArrayOfObjects(products, 'Underartikkelgr.')

  for (let i = 0; i < regions.length; i++) {
    let region = regions[i]

    if (region === null || region.length === 0) {
      region = 'Øvrige'
    }

    const regionProducts = {
      region: region,
      products: []
    }

    products.filter((product) => {
      // Insert products with region
      if (product['Underartikkelgr.'] === null || product['Underartikkelgr.'].length === 0) {
        product['Underartikkelgr.'] = 'Øvrige'
      }

      if (product['Underartikkelgr.'] === region) {
        regionProducts.products = [...regionProducts.products, product]
      }
    })

    productsByRegion = [...productsByRegion, regionProducts]
  }

  return productsByRegion
}

/**
 * Categorize all regions by country.
 *
 * @param {Array} products
 * @returns {Array} regionsByCountry
 */
const getRegionsByCountry = (products) => {
  const regionByCountry = {}

  products.forEach((product) => {
    const country = product.Navn
    const region = product['Underartikkelgr.']

    // Skip unnamed regions
    if (region.length === 0) {
      return
    }

    if (!regionByCountry[country]) {
      regionByCountry[country] = []
    }

    regionByCountry[country] = [...regionByCountry[country], region]
  })

  return regionByCountry
}

/**
 * Categorize all countries by type.
 *
 * @param {Array} products
 * @returns {Array} countriesByType
 */
const getCountriesByType = (products) => {
  const countriesByType = {}

  products.forEach((product) => {
    const type = product.Hovedgruppe
    const country = product.Navn

    if (!countriesByType[type]) {
      countriesByType[type] = []
    }

    countriesByType[type] = [...countriesByType[type], country]
  })

  return countriesByType
}

/**
 * Get distinct values from array of objects.
 *
 * @param {Array} array
 * @param {String} key
 * @returns Array of objects with distinct values
 */
const distinctArrayOfObjects = (array, key) => {
  const values = array.map((object) => {
    return object[key]
  })

  return [...new Set(values)]
}

/**
 * Get only distinct values from an array.
 *
 * @param {Array} array
 * @returns Array of distinct values
 */
const distinctArray = (array) => {
  return [...new Set(array)]
}

/**
 * Build a products table for PDF export.
 *
 * @param {Array} products
 * @returns {Object} table
 */
const createProductsTable = (products, stock, region) => {
  const rows = []

  // Insert table headers
  rows.push(Object.values(Column))

  // Filter out columns we don't want to export
  const filteredProducts = filterColumns(products)

  filteredProducts.sort((a, b) => {
    // Sort by producer
    if (a[Column.Producer] < b[Column.Producer]) return -1
    if (a[Column.Producer] > b[Column.Producer]) return 1

    // Sort by product name
    if (a[Column.ProductName] < b[Column.ProductName]) return -1
    if (a[Column.ProductName] > b[Column.ProductName]) return 1

    // Sort by price
    if (a[Column['Pris eks.mva']] < b[Column['Pris eks.mva']]) return -1
    if (a[Column['Pris eks.mva']] > b[Column['Pris eks.mva']]) return 1

    // Sort by year
    if (a[Column['Årgang']] === 'NV') return -1
    if (a[Column['Årgang']] > b[Column['Årgang']]) return -1
    if (a[Column['Årgang']] < b[Column['Årgang']]) return 1

    // Sort by volume
    if (a[Column.Vol] < b[Column.Vol]) return -1
    if (a[Column.Vol] > b[Column.Vol]) return 1
  })

  // Build table rows
  filteredProducts.forEach((product) => {
    const row = []
    let inStock = false

    if (stock) {
      const productStock = stock.find((item) => {
        return parseInt(item.Material) === parseInt(product[Column.SAPnr])
      })

      inStock = (!!productStock && productStock.PCS > 0)
    }

    Object.keys(product).forEach((key) => {
      if (key === Column.SAPnr || key === Column.EPDnr) {
        row.push({
          text: product[key],
          color: inStock ? 'black' : 'red'
        })
      } else if (key === Column.Price) {
        row.push({
          text: product[key],
          alignment: 'right'
        })
      } else {
        row.push({
          text: product[key],
          color: 'black'
        })
      }
    })

    rows.push(row)
  })

  // Insert region for table
  const regionHeader = [
    {
      text: region,
      rowSpan: 1,
      border: [false, false, false, false],
      style: 'region',
      colSpan: 7,
      alignment: 'left'
    },
    {},
    {},
    {},
    {},
    {},
    {},
    {}
  ]

  // Add regionHeader to start of array
  rows.unshift(regionHeader)

  // Setup table object for pdfmake
  const table = {
    layout: 'lightHorizontalLines',
    table: {
      dontBreakRows: true,
      headerRows: 2,
      keepWithHeaderRows: 1,
      body: rows,
      widths: ['auto', 'auto', 150, 'auto', 80, 40, 'auto', 50]
    },
    margin: [0, 0, 0, 20]
  }

  return table
}

/**
 * Filter out columns we don't want to export.
 *
 * @param {Array} products
 * @returns Array of products with filtered columns
 */
const filterColumns = (products) => {
  const columns = Object.values(Column)

  const filteredProducts = products.map((product) => {
    const filteredProduct = {}

    columns.forEach((column) => {
      filteredProduct[column] = product[column]
    })

    return filteredProduct
  })

  return filteredProducts
}

/**
 * Rename the columns we want to export to PDF table.
 *
 * @param {Array} products
 * @returns void
 */
const renameColumns = (products) => {
  for (let i = 0; i < products.length; i++) {
    // Copy values from old keys to new keys
    products[i][Column.SAPnr] = products[i]['Artikkelnr.']
    products[i][Column.EPDnr] = products[i]['PLU-nr.']
    products[i][Column.ProductName] = products[i].Artikkel
    products[i][Column.Year] = products[i].Aargang
    products[i][Column.Producer] = products[i].Prod
    products[i][Column.Importer] = products[i].Artikkeltype
    products[i][Column.Volume] = products[i]['Art. vekt']
    products[i][Column.Price] = products[i].Utpris

    // Delete the old keys
    delete products[i]['Artikkelnr.']
    delete products[i]['PLU-nr.']
    delete products[i].Artikkel
    delete products[i].Aargang
    delete products[i].Prod
    delete products[i].Artikkeltype
    delete products[i]['Art. vekt']
    delete products[i].Utpris
  }
}

const formatProductPrice = (products) => {
  for (let i = 0; i < products.length; i++) {
    if (products[i][Column.Price] === null) continue

    const currencyFormatter = new Intl.NumberFormat('nb-NO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })

    products[i][Column.Price] = currencyFormatter.format(products[i][Column.Price])
  }
}

const renameProductTypes = (products) => {
  for (let i = 0; i < products.length; i++) {
    const productType = products[i].Hovedgruppe

    switch (productType) {
      case 'Vin Musserende':
      case 'Musserende':
        products[i].Hovedgruppe = ProductType.SparklingWine
        break
      case 'Vin Hvit':
        products[i].Hovedgruppe = ProductType.WhiteWine
        break
      case 'Vin Rose':
      case 'Rosevin':
        products[i].Hovedgruppe = ProductType.RoseWine
        break
      case 'Vin Rød':
        products[i].Hovedgruppe = ProductType.RedWine
        break
      case 'Vin Sterk':
        products[i].Hovedgruppe = ProductType.StrongWine
        break
      case 'Vin Dessert':
        products[i].Hovedgruppe = ProductType.DessertWine
        break
      case 'Brennevin':
        products[i].Hovedgruppe = ProductType.Liqour
        break
      case 'Sterkvit':
        products[i].Hovedgruppe = ProductType.StrongWine
        break
      case 'Cider':
        products[i].Hovedgruppe = ProductType.Cider
        break
      case 'Alkoholfritt':
        products[i].Hovedgruppe = ProductType.NonAlcoholic
    }
  }

  return products
}

/**
 * Convert file to base64
 *
 * @param {String} url
 * @returns {String} base64 string
 */
const toDataURL = url => fetch(url)
  .then(response => response.blob())
  .then(blob => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  }))

/**
 * Build and download the PDF.
 *
 * @param {Array} content
 */
const buildPDF = async (content) => {
  const currentDate = new Date()
  const dateForFileName = currentDate.getDate() + '-' + (currentDate.getMonth() + 1) + '-' + currentDate.getFullYear()
  const defaultFileName = `belmonte-pricelist-${dateForFileName}`

  const docDefinition = {
    info: {
      title: defaultFileName
    },
    footer: {
      columns: [
        {
          text: [
            {
              text: '- Linjer markert i rødt er utsolgt    ',
              alignment: 'left',
              color: 'red'
            },
            {
              text: '* Veiledende pris - prisavvik kan oppstå avhengig av grossist og om produktet er lagerført eller skaffevare.',
              alignment: 'left',
              color: 'blue'
            }
          ],
          margin: [40, 0, 0, 0]
        }
      ],
      margin: [0, 10, 0, 0]
    },
    content: content,
    styles: {
      type: {
        fontSize: 18,
        bold: true,
        lineHeight: 2
      },
      country: {
        fontSize: 16,
        bold: true,
        lineHeight: 1.5
      },
      region: {
        fontSize: 12,
        bold: true,
        lineHeight: 1.2
      }
    },
    defaultStyle: {
      fontSize: 7,
      bold: false
    }
  }

  pdfMake.createPdf(docDefinition).download(defaultFileName)
}
